import React, { useEffect, useState } from 'react'
import useSWR from 'swr'
import useUser from '../../hooks/User'
import axios from 'axios'
import useAccessToken from '../../hooks/AccessToken'
import moment from 'moment-timezone'
import InfoLimitDatabase from './InfoLimitDatabase'
import PurchaseItemDatabase from './PurchaseItemDatabase'
import { useIntl } from 'react-intl'
import { CompanyLimitData } from '../../interfaces/CompanyLimitData'
import useDynamicTab from '../../hooks/useDynamicTab'
import { useCompanyStore } from '../../stores/CompanyStore'
import { useCompanyLimitStore } from '../../stores/CompanyLimitStore'

const API_URL = process.env.REACT_APP_API_URL

const MasterManageAccount = () => {
  const user = useUser()
  const token = useAccessToken()
  const [activeTab, setActiveTab] = useState('infoLimitDatabase')
  const intl = useIntl()
  const { companies } = useCompanyStore()
  const { companyLimit } = useCompanyLimitStore()

  const fetcher = async (url: string) => {
    const response = await axios.get(url, {
      headers: {
        'X-Auth-token': token,
      },
    });
    return response.data.data;
  };

  const handleClickTab = (tab: string) => {
    setActiveTab(tab)
  }

  useDynamicTab({ title: `${intl.formatMessage({ id: "FORM.LABEL.MANAGE_ACCOUNT" })} | CRM WooBlazz` });

  return (
    <>
      <h4 className='my-3'>{intl.formatMessage({ id: "FORM.LABEL.MANAGE_ACCOUNT" })}</h4>
      <div className='card mb-5 mb-xxl-8'>
        <div className="card-body pt-9 pb-0">
          <div className="d-flex flex-column" id="kt_post">
            <div className="d-flex flex-wrap flex-sm-nowrap">
              <div className="me-7 mb-4">
                <div className="symbol symbol-100px symbol-lg-160px symbol-fixed position-relative">
                  {
                    user.data.profile_picture !== null ?
                      <img src={user.data.profile_picture} className='profile-picture' alt="img profile" />
                      :
                      <img src="media/icons/duotune/communication/com013.svg" alt="img profile" />
                  }
                </div>
              </div>
              <div className="flex-grow-1">
                <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                  <div className="d-flex flex-column">
                    {
                      user.data.isWaSessionActive === undefined ?
                        <span className="badge badge-light-danger fw-bolder" style={{ width: 'fit-content' }}>
                          <i className="fas fa-link me-2 fs-8"></i> ?
                        </span >
                        :
                        user.data.isWaSessionActive ?
                          <span className="badge badge-light-success fw-bolder" style={{ width: 'fit-content' }}>
                            <i className="fas fa-link me-2 fs-8"></i> {intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CONNECT_CHAT' })}
                          </span >
                          :
                          <span className="badge badge-light-danger fw-bolder" style={{ width: 'fit-content' }}>
                            <i className="fas fa-unlink me-2 fs-8"></i> {intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DISCONNECT_CHAT' })}
                          </span>
                    }
                    <div className="d-flex align-items-center">
                      <span className="text-gray-900 fs-2 fw-bolder me-1">{user.data.name}</span>
                    </div>
                    <div className="d-flex flex-wrap fw-bold fs-6 mb-4 pe-2">
                      <div className="d-flex align-items-center text-gray-400 me-5 mb-2">
                        <span className="svg-icon svg-icon-4 me-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path opacity="0.3" d="M22 12C22 17.5 17.5 22 12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2C17.5 2 22 6.5 22 12ZM12 7C10.3 7 9 8.3 9 10C9 11.7 10.3 13 12 13C13.7 13 15 11.7 15 10C15 8.3 13.7 7 12 7Z" fill="black"></path>
                            <path d="M12 22C14.6 22 17 21 18.7 19.4C17.9 16.9 15.2 15 12 15C8.8 15 6.09999 16.9 5.29999 19.4C6.99999 21 9.4 22 12 22Z" fill="black"></path>
                          </svg>
                        </span>
                        <span>{user.data.username}</span>
                      </div>
                      <div className="d-flex align-items-center text-gray-400 me-5 mb-2">
                        <span className="svg-icon svg-icon-4 me-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path opacity="0.3" d="M20.9 12.9C20.3 12.9 19.9 12.5 19.9 11.9C19.9 11.3 20.3 10.9 20.9 10.9H21.8C21.3 6.2 17.6 2.4 12.9 2V2.9C12.9 3.5 12.5 3.9 11.9 3.9C11.3 3.9 10.9 3.5 10.9 2.9V2C6.19999 2.5 2.4 6.2 2 10.9H2.89999C3.49999 10.9 3.89999 11.3 3.89999 11.9C3.89999 12.5 3.49999 12.9 2.89999 12.9H2C2.5 17.6 6.19999 21.4 10.9 21.8V20.9C10.9 20.3 11.3 19.9 11.9 19.9C12.5 19.9 12.9 20.3 12.9 20.9V21.8C17.6 21.3 21.4 17.6 21.8 12.9H20.9Z" fill="black" />
                            <path d="M16.9 10.9H13.6C13.4 10.6 13.2 10.4 12.9 10.2V5.90002C12.9 5.30002 12.5 4.90002 11.9 4.90002C11.3 4.90002 10.9 5.30002 10.9 5.90002V10.2C10.6 10.4 10.4 10.6 10.2 10.9H9.89999C9.29999 10.9 8.89999 11.3 8.89999 11.9C8.89999 12.5 9.29999 12.9 9.89999 12.9H10.2C10.4 13.2 10.6 13.4 10.9 13.6V13.9C10.9 14.5 11.3 14.9 11.9 14.9C12.5 14.9 12.9 14.5 12.9 13.9V13.6C13.2 13.4 13.4 13.2 13.6 12.9H16.9C17.5 12.9 17.9 12.5 17.9 11.9C17.9 11.3 17.5 10.9 16.9 10.9Z" fill="black" />
                          </svg>
                        </span>
                        <span>Exp. {moment(companies.date_expired).format('DD MMMM YYYY')}</span>
                      </div>
                      <div className="d-flex align-items-center text-gray-400 mb-2">
                        <span className="svg-icon svg-icon-4 me-1">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path opacity="0.3" d="M21 19H3C2.4 19 2 18.6 2 18V6C2 5.4 2.4 5 3 5H21C21.6 5 22 5.4 22 6V18C22 18.6 21.6 19 21 19Z" fill="black"></path>
                            <path d="M21 5H2.99999C2.69999 5 2.49999 5.10005 2.29999 5.30005L11.2 13.3C11.7 13.7 12.4 13.7 12.8 13.3L21.7 5.30005C21.5 5.10005 21.3 5 21 5Z" fill="black"></path>
                          </svg>
                        </span>
                        <span>{companyLimit.companyEmail ?? "-"}</span>
                      </div>
                    </div>
                    <div className="fw-bold fs-6 mb-4 pe-2">
                      <div className="d-flex align-items-center text-gray-400 mb-1 gap-1">
                        <span>{intl.formatMessage({ id: 'FORM.LABEL.PHONE' })} : </span>
                        <span>{user.data.phone}</span>
                      </div>
                      <div className="d-flex align-items-center text-gray-400 mb-1 gap-1">
                        <span>{intl.formatMessage({ id: 'FORM.LABEL.CITY' })} : </span>
                        <span>{companyLimit.companyCityName ?? "-"}</span>
                      </div>
                      <div className="d-flex align-items-center text-gray-400 mb-1 gap-1">
                        <span>{intl.formatMessage({ id: 'FORM.LABEL.ADDRESS' })} : </span>
                        <span>{companyLimit.companyAddress ?? "-"}</span>
                      </div>
                      <div className="d-flex align-items-center text-gray-400 mb-1 gap-1">
                        <span>NPWP : </span>
                        <span>{companyLimit.companyNpwp ?? "-"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* <ul className="nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bolder">
            <li className="nav-item mt-2">
                <p
                  className={`nav-link text-active-primary ms-0 me-10 py-5 cursor-pointer ${activeTab === 'infoLimitDatabase' ? 'active' : ''}`}
                  onClick={() => handleClickTab('infoLimitDatabase')}
                >Info</p>
              </li>
            <li className="nav-item mt-2">
                <p
                  className={`nav-link text-active-primary ms-0 me-10 py-5 cursor-pointer ${activeTab === 'purchaseItemDatabase' ? 'active' : ''}`}
                  onClick={() => handleClickTab('purchaseItemDatabase')}
                >{intl.formatMessage({ id: "FORM.LABEL.PURCHASE" })}</p>
              </li>
          </ul> */}
        </div>
      </div>

      {
        activeTab === 'infoLimitDatabase' && <InfoLimitDatabase data={companyLimit} isLoadingCompanyLimitData={Object.keys(companyLimit).length === 0} />
      }
      {/* {
          activeTab === 'purchaseItemDatabase' && <PurchaseItemDatabase />
        } */}
    </>
  )
}

export default MasterManageAccount